import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import Modal from "../../../../components/Modal";
import Button from "../../../../components/Button";
import Switch from "../../../../components/Switch";
import Input from "../../../../components/Input";
import { APP_ERROR_MESSAGES, Regex } from "../../../../utils/appConstant";

interface ConfigurationModalProps {
  open: boolean;
  onClose: () => void;
  onSave: (includeMatTax: boolean, includeAdjustments: boolean, platformCommission: number | null) => void;
  initialIncludeMatTax: boolean;
  initialIncludeAdjustments: boolean;
  initialPlatformCommission: number | null | undefined;
  loading?: boolean;
  view?: boolean;
}

const ConfigurationModal: React.FC<ConfigurationModalProps> = ({
  open,
  onClose,
  onSave,
  initialIncludeMatTax,
  initialIncludeAdjustments,
  initialPlatformCommission,
  loading = false,
  view = false,
}) => {
  const { t } = useTranslation();
  const [includeMatTax, setIncludeMatTax] = useState(initialIncludeMatTax);
  const [includeAdjustments, setIncludeAdjustments] = useState(initialIncludeAdjustments);
  const [platformCommission, setPlatformCommission] = useState<string>(
    initialPlatformCommission !== null && initialPlatformCommission !== undefined
      ? Number(initialPlatformCommission).toFixed(2)
      : '0'
  );
  const [platformCommissionError, setPlatformCommissionError] = useState<string>('');

  useEffect(() => {
    setIncludeMatTax(initialIncludeMatTax);
    setIncludeAdjustments(initialIncludeAdjustments);
    setPlatformCommission(
      initialPlatformCommission !== null && initialPlatformCommission !== undefined
        ? Number(initialPlatformCommission).toFixed(2)
        : '0'
    );
    setPlatformCommissionError('');
  }, [initialIncludeMatTax, initialIncludeAdjustments, initialPlatformCommission, open]);

  const handleCancel = () => {
    setIncludeMatTax(initialIncludeMatTax);
    setIncludeAdjustments(initialIncludeAdjustments);
    setPlatformCommission(
      initialPlatformCommission !== null && initialPlatformCommission !== undefined
        ? Number(initialPlatformCommission).toFixed(2)
        : '0'
    );
    setPlatformCommissionError('');
    onClose();
  };

  const validatePlatformCommission = (value: string): string => {
    const raw = value as any;
    const str = raw?.toString?.().trim?.() ?? '';

    if (!str) {
      return APP_ERROR_MESSAGES.reservations.edit.blankPlatformCommission;
    } else {
      if (!Regex.percentPattern.test(str)) {
        return APP_ERROR_MESSAGES.reservations.edit.invalidPlatformCommission;
      } else {
        const numValue = parseFloat(str);
        if (Number.isNaN(numValue) || numValue < 0 || numValue > 100) {
          return APP_ERROR_MESSAGES.reservations.edit.invalidPlatformCommission;
        }
      }
    }
    
    return '';
  };

  const handlePlatformCommissionChange = (e: any) => {
    setPlatformCommission(e.target.value);
    const error = validatePlatformCommission(e.target.value);
    setPlatformCommissionError(error);
  };

  const handleSave = () => {
    const error = validatePlatformCommission(platformCommission);
    if (error) {
      setPlatformCommissionError(error);
      return;
    }
    
    const commissionValue = platformCommission.trim() === '' ? null : Number(Number(platformCommission).toFixed(2)) || 0;
    onSave(includeMatTax, includeAdjustments, commissionValue);
  };

  const modalAction = (
    <>
      {!view && (
        <Button
          className="btn btn-primary me-3"
          text={t('reservations.edit.pageTexts.form.buttons.save')}
          onClick={handleSave}
          loading={loading}
        />
      )}
      <Button
        className="btn btn-outline-secondary me-3"
        text={t('reservations.edit.pageTexts.form.buttons.cancel')}
        onClick={handleCancel}
        disabled={loading}
      />
    </>
  );

  return (
    <Modal
      open={open}
      onClose={handleCancel}
      title={t('reservations.edit.pageTexts.form.configurationModal.title')}
      titleClassName="text-start"
      content={
        <div className="row g-4">
          <div className="col-md-6">
            <div className="form-floating form-floating-outline">
              <Input
                type="text"
                name="platformCommission"
                value={platformCommission}
                onChange={handlePlatformCommissionChange}
                label={t('reservations.edit.pageTexts.form.configurationModal.platformCommissionLabel')}
                placeholder={t('reservations.edit.pageTexts.form.configurationModal.platformCommission')}
                disabled={view}
                required
                error={!!platformCommissionError}
                errorMessage={platformCommissionError}
              />
            </div>
          </div>
          <div className="col-md-6">
            <div className="mb-4">
              <Switch
                label={t('reservations.edit.pageTexts.form.configurationModal.includeMatTax')}
                name="includeMatTax"
                checked={includeMatTax}
                onChange={(e: any) => setIncludeMatTax(e.target.checked)}
                disabled={view}
              />
            </div>
            <div>
              <Switch
                label={t('reservations.edit.pageTexts.form.configurationModal.includeAdjustments')}
                name="includeAdjustments"
                checked={includeAdjustments}
                onChange={(e: any) => setIncludeAdjustments(e.target.checked)}
                disabled={view}
              />
            </div>
          </div>
        </div>
      }
      action={modalAction}
      actionClassName="justify-content-start"
      maxWidth="sm"
    />
  );
};

export default ConfigurationModal;

